import { Card, CardContent, CardHeader, CardTitle } from "ui/card";
import { Button } from "ui/button";
import { Badge } from "ui/badge";
import { Plus, X, Check, Settings, Truck, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  useSensors,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { TourServiceMasterItem } from "services/tourApprovalService";

interface Service {
  id: string;
  publicId: string;
  name: string;
  price: number;
  capacity: number | null;
  type: string;
}

interface ServiceApplicant {
  id: string;
  name: string;
  itsNumber: string;
  age: number;
  photo?: string;
}

interface ServicesProps {
  applicants: ServiceApplicant[];
  serviceMaster: TourServiceMasterItem[];
  applicantServices: Record<string, string[]>;

  setApplicantServices: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;

  saveApplicantServices: (
    updatedApplicantServices: Record<string, string[]>,
  ) => Promise<boolean>;

  carUnits: {
    id: string;
    applicants: string[];
  }[];

  setCarUnits: React.Dispatch<
    React.SetStateAction<
      {
        id: string;
        applicants: string[];
      }[]
    >
  >;

  dragOverApplicant: string | null;
  setDragOverApplicant: React.Dispatch<React.SetStateAction<string | null>>;

  dragOverCar: string | null;
  setDragOverCar: React.Dispatch<React.SetStateAction<string | null>>;

  sensors: ReturnType<typeof useSensors>;

  handleRemoveService: (applicantId: string, serviceId: string) => void;

  handleServiceDragEnd: (event: DragEndEvent, applicantId: string) => void;
}

interface SortableServiceItemProps {
  serviceId: string;
  service: {
    id: string;
    name: string;
    price: number;
    capacity: number | null;
    type: string;
  };
  onRemove: () => void;
  isMandatory: boolean;
  isIncluded: boolean;
}

function SortableServiceItem({
  serviceId,
  service,
  onRemove,
  isMandatory,
  isIncluded,
}: SortableServiceItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: serviceId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const canRemove = !isMandatory && !isIncluded;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-wrap sm:flex-nowrap items-center gap-2 p-2 bg-white rounded border hover:border-gray-300 transition-colors"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-3.5 w-3.5 text-gray-400" />
      </div>
      <div className="flex items-center gap-2 flex-1">
        <Check className="h-3.5 w-3.5 text-green-600" />
        <span className="text-sm font-medium text-gray-900">
          {service.name}
        </span>
        {isMandatory && (
          <Badge className="bg-red-100 text-red-700 border-red-300 text-xs px-1.5 py-0">
            Mandatory
          </Badge>
        )}
        {isIncluded && (
          <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs px-1.5 py-0">
            Included
          </Badge>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {service.price > 0 && (
          <span className="text-xs text-gray-600">${service.price}</span>
        )}
        {canRemove && (
          <button
            onClick={onRemove}
            className="p-1 hover:bg-red-50 rounded transition-colors"
          >
            <X className="h-3.5 w-3.5 text-red-600" />
          </button>
        )}
      </div>
    </div>
  );
}

function ApplicantDraggable({
  applicantId,
  children,
}: {
  applicantId: string;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `applicant-${applicantId}`,
    });

  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    opacity: isDragging ? 0.65 : 1,
    position: "relative",
    zIndex: isDragging ? 50 : "auto",
    touchAction: "none",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="touch-none cursor-grab active:cursor-grabbing"
    >
      {children}
    </div>
  );
}

function ServiceDraggable({ service }: { service: Service }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `service-${service.id}`,
    });

  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    opacity: isDragging ? 0.65 : 1,
    position: "relative",
    zIndex: isDragging ? 50 : "auto",
    touchAction: "none",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="rounded-lg border-2 border-gray-200 bg-white p-2.5 cursor-grab touch-none transition-all hover:border-blue-300 hover:bg-blue-50/50 active:cursor-grabbing"
    >
      <div className="flex items-start gap-2">
        <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />

        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-gray-900 mb-1">
            {service.name}
          </div>

          <div className="flex items-center gap-1.5">
            <Badge className="bg-gray-100 text-gray-700 border-gray-300 text-xs px-1.5 py-0">
              Optional
            </Badge>

            {service.price > 0 && (
              <span className="text-xs font-semibold text-gray-700">
                ${service.price}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ApplicantDropZone({
  applicantId,
  children,
}: {
  applicantId: string;
  children: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({
    id: `drop-applicant-${applicantId}`,
  });

  return <div ref={setNodeRef}>{children}</div>;
}

function CarDropZone({
  carId,
  children,
}: {
  carId: string;
  children: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({
    id: `drop-car-${carId}`,
  });

  return <div ref={setNodeRef}>{children}</div>;
}

export default function Services({
  applicants,
  serviceMaster,
  applicantServices,
  setApplicantServices,
  saveApplicantServices,
  carUnits,
  setCarUnits,
  dragOverApplicant,
  setDragOverApplicant,
  dragOverCar,
  setDragOverCar,
  sensors,
  handleRemoveService,
  handleServiceDragEnd,
}: ServicesProps) {
  const mandatoryIncludedServices = serviceMaster
    .filter((s) => s.isMandatory && s.isDefaultChecked && s.isActive)
    .map((service) => ({
      id: service.tourServiceId.toString(),
      publicId: service.publicId,
      name: service.serviceName,
      type: "Included",
      price: 0,
      capacity: null,
    }));

  const optionalServices = serviceMaster
    .filter((s) => !s.isMandatory && s.isDefaultChecked && s.isActive)
    .map((service) => ({
      id: service.tourServiceId.toString(),
      publicId: service.publicId,
      name: service.serviceName,
      type: "Optional",
      price: 0,
      capacity: null,
    }));

  const handleGlobalDragEnd = async (event: DragEndEvent) => {
  const { active, over } = event;

  if (!over) return;

  const activeId = String(active.id);
  const overId = String(over.id);

  // Applicant -> Car
  if (
    activeId.startsWith("applicant-") &&
    overId.startsWith("drop-car-")
  ) {
    // ...your existing car code...
  }

  // Service -> Applicant
  if (
    activeId.startsWith("service-") &&
    overId.startsWith("drop-applicant-")
  ) {
    const serviceId = activeId.replace("service-", "");
    const applicantId = overId.replace("drop-applicant-", "");

    const existingServices = applicantServices[applicantId] || [];

    if (existingServices.includes(serviceId)) {
      const serviceName =
        serviceMaster.find(
          (s) => s.tourServiceId.toString() === serviceId
        )?.serviceName || "Service";

      toast.error(`${serviceName} is already assigned`);
      return;
    }

    const updatedApplicantServices = {
      ...applicantServices,
      [applicantId]: [
        ...(applicantServices[applicantId] || []),
        serviceId,
      ],
    };

    setApplicantServices(updatedApplicantServices);

    try {
      const saved = await saveApplicantServices(
        updatedApplicantServices
      );

      if (!saved) {
        setApplicantServices(applicantServices);
        return;
      }

      toast.success("Add-on assigned to applicant");
    } catch (error) {
      console.error("Failed to save applicant service:", error);

      setApplicantServices(applicantServices);

      toast.error("Failed to save service");
    }

    return;
  }
};

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      autoScroll
      onDragOver={({ active, over }) => {
        const activeId = String(active.id);
        const overId = over ? String(over.id) : "";
        setDragOverCar(
          activeId.startsWith("applicant-") && overId.startsWith("drop-car-")
            ? overId.replace("drop-car-", "")
            : null,
        );
        setDragOverApplicant(
          activeId.startsWith("service-") &&
            overId.startsWith("drop-applicant-")
            ? overId.replace("drop-applicant-", "")
            : null,
        );
      }}
      onDragEnd={(event) => {
        setDragOverApplicant(null);
        setDragOverCar(null);
        handleGlobalDragEnd(event);
      }}
      onDragCancel={() => {
        setDragOverApplicant(null);
        setDragOverCar(null);
      }}
    >
      <div className="space-y-4 overflow-x-hidden">
        {applicants.length === 0 ? (
          <Card className="border-amber-200">
            <CardContent className="p-6">
              <div className="text-center py-8 text-gray-500">
                <Settings className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No applicants added to tour yet</p>
                <p className="text-sm mt-2">
                  Please add applicants from the Applicants tab first
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {/* ── Mandatory & Included — shown once for ALL applicants ── */}
            <Card
              className="border-emerald-200 bg-gradient-to-r from-emerald-50/60 to-green-50/60"
              style={{ gap: "0px" }}
            >
              <CardContent className="p-2.5">
                <p className="mb-2 text-xs font-semibold text-emerald-800">
                  Included Services
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                  {mandatoryIncludedServices.map((service) => (
                    <div
                      key={service.id}
                      className="flex items-center gap-2 rounded-md border border-emerald-200 bg-white px-2.5 py-1.5"
                    >
                      <Check className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="truncate text-xs font-medium text-gray-900">
                          {service.name}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-[440px_minmax(0,1fr)] xl:grid-rows-[auto_auto]">
              {/* ── Private Car — Group service (1 car = 4 pax, charged per car) ── */}
              <Card
                className="border-violet-200 bg-white xl:col-start-1 xl:row-start-1"
                style={{ gap: "0px" }}
              >
                <CardHeader className="p-3 pb-2">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-violet-900">
                        <Truck className="h-4 w-4 text-violet-600" />
                        Private Car — Group Service
                        <Badge className="shrink-0 whitespace-nowrap bg-violet-100 text-xs text-violet-700 border-violet-300">
                          $150 / car
                        </Badge>
                      </CardTitle>
                      <p className="mt-1 text-[11px] text-gray-600">
                        Drop applicant cards here · 4 applicants per car
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 border-violet-300 px-2.5 text-xs text-violet-700 hover:bg-violet-50"
                      onClick={() =>
                        setCarUnits((prev) => [
                          ...prev,
                          { id: `car-${prev.length + 1}`, applicants: [] },
                        ])
                      }
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add Car
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
                    {carUnits.map((car, carIdx) => {
                      const isCarOver = dragOverCar === car.id;
                      return (
                        <div
                          key={car.id}
                          className={`rounded-lg border-2 p-2.5 transition-all ${isCarOver ? "border-violet-400 bg-violet-50 shadow-sm" : "border-violet-100"}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-violet-800">
                              Car {carIdx + 1}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-violet-600">
                                {car.applicants.length}/4 pax · $150
                              </span>
                              {carIdx > 0 && (
                                <button
                                  onClick={() =>
                                    setCarUnits((prev) =>
                                      prev.filter((c) => c.id !== car.id),
                                    )
                                  }
                                  className="p-0.5 hover:bg-red-50 rounded text-red-500"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          </div>

                          <CarDropZone carId={car.id}>
                            <div className="space-y-1 min-h-[60px]">
                              {car.applicants.length === 0 ? (
                                <div
                                  className={`text-center py-3 border-2 border-dashed rounded text-xs text-gray-400 ${isCarOver ? "border-violet-400" : "border-gray-200"}`}
                                >
                                  Drag applicants here
                                </div>
                              ) : (
                                car.applicants.map((aId) => {
                                  const app = applicants.find(
                                    (a) => String(a.id) === String(aId),
                                  );

                                  if (!app) return null;
                                  return (
                                    <div
                                      key={aId}
                                      className="flex items-center gap-2 bg-white rounded border border-violet-200 px-2 py-1"
                                    >
                                      <div className="w-5 h-5 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                                        <span className="text-[8px] font-medium text-gray-600">
                                          {app.name
                                            .split(" ")
                                            .filter(Boolean)
                                            .slice(0, 2)
                                            .map((name) => name[0])
                                            .join("")
                                            .toUpperCase()}
                                        </span>
                                      </div>
                                      <span className="text-xs text-gray-700 truncate flex-1">
                                        {app.name}
                                      </span>
                                      <button
                                        onClick={() =>
                                          setCarUnits((prev) =>
                                            prev.map((c) =>
                                              c.id === car.id
                                                ? {
                                                    ...c,
                                                    applicants:
                                                      c.applicants.filter(
                                                        (id) => id !== aId,
                                                      ),
                                                  }
                                                : c,
                                            ),
                                          )
                                        }
                                        className="text-red-400 hover:text-red-600"
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </div>
                                  );
                                })
                              )}
                              {car.applicants.length > 0 &&
                                car.applicants.length < 4 && (
                                  <div
                                    className={`text-center py-1.5 border border-dashed rounded text-xs text-gray-300 ${isCarOver ? "border-violet-300 text-violet-400" : "border-gray-200"}`}
                                  >
                                    {4 - car.applicants.length} seat
                                    {4 - car.applicants.length !== 1
                                      ? "s"
                                      : ""}{" "}
                                    free
                                  </div>
                                )}
                            </div>
                          </CarDropZone>
                        </div>
                      );
                    })}
                  </div>
                  {carUnits.some((c) => c.applicants.length > 0) && (
                    <div className="mt-3 pt-3 border-t flex flex-col gap-1 sm:flex-row sm:justify-between text-sm">
                      <span className="text-gray-600">
                        Total Car Cost (
                        {carUnits.filter((c) => c.applicants.length > 0).length}{" "}
                        car
                        {carUnits.filter((c) => c.applicants.length > 0)
                          .length !== 1
                          ? "s"
                          : ""}
                        ):
                      </span>
                      <span className="font-semibold text-violet-900">
                        $
                        {carUnits.filter((c) => c.applicants.length > 0)
                          .length * 150}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ── Optional Services — draggable per applicant ── */}
              <div className="grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-[210px_minmax(0,1fr)] xl:contents">
                {/* Left sidebar — optional services only */}
                <div className="space-y-3 overflow-hidden xl:col-start-1 xl:row-start-2">
                  <Card
                    className="border-gray-200 bg-white shadow-sm"
                    style={{ gap: "0px" }}
                  >
                    <CardHeader className="p-3 pb-2">
                      <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <Settings className="h-4 w-4 text-gray-600" />
                        Optional Add-ons
                      </CardTitle>
                      <p className="text-xs text-gray-500 mt-1">
                        Drag to assign to individual applicants
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-2 p-3 pt-0">
                      {optionalServices.map((service) => (
                        <ServiceDraggable key={service.id} service={service} />
                      ))}
                    </CardContent>
                  </Card>
                </div>

                {/* Right side — applicants */}
                <div className="grid max-h-[calc(100vh-245px)] grid-cols-1 gap-2 overflow-y-auto pr-2 xl:col-start-2 xl:row-span-2 xl:row-start-1 2xl:grid-cols-2">
                  <div className="col-span-full mb-0.5 flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-semibold text-violet-800">
                    <GripVertical className="h-4 w-4 shrink-0 text-violet-600" />
                    <span>
                      Drag an applicant card into a car, or drag an add-on onto
                      an applicant.
                    </span>
                  </div>
                  {applicants.map((applicant) => {
                    const applicantId = String(applicant.id);
                    const assignedServices = (
                      applicantServices[applicantId] || []
                    ).filter((sid) =>
                      optionalServices.some((service) => service.id === sid),
                    );
                    const isDragOver = dragOverApplicant === applicantId;
                    return (
                      <ApplicantDropZone
                        key={applicantId}
                        applicantId={applicantId}
                      >
                        <ApplicantDraggable applicantId={applicantId}>
                          <Card
                            className={`h-full border-2 transition-all ${
                              isDragOver
                                ? "border-blue-400 bg-blue-50/30 shadow-md"
                                : "border-gray-200"
                            }`}
                          >
                            <CardContent className="p-2.5">
                              <div className="mb-1.5 flex items-center gap-2 border-b pb-1.5">
                                <div className="h-8 w-8 rounded-full border-2 border-gray-200 bg-gray-100 flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-medium text-gray-600">
                                    {applicant.name
                                      .split(" ")
                                      .filter(Boolean)
                                      .slice(0, 2)
                                      .map((name) => name[0])
                                      .join("")
                                      .toUpperCase()}
                                  </span>
                                </div>

                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm font-semibold text-gray-900">
                                      {applicant.name}
                                    </span>
                                  </div>

                                  <div className="text-xs text-gray-500">
                                    ITS: {applicant.itsNumber} · {applicant.age}
                                    y
                                  </div>
                                </div>

                                <div className="hidden text-[10px] text-violet-500 sm:block">
                                  Drag to car ←
                                </div>
                              </div>

                              <div>
                                {assignedServices.length === 0 ? (
                                  <div
                                    className={`rounded border-2 border-dashed py-2 text-center text-xs text-gray-400 ${
                                      isDragOver
                                        ? "border-blue-300 bg-blue-50/50"
                                        : "border-gray-200"
                                    }`}
                                  >
                                    {isDragOver
                                      ? "Drop optional service here"
                                      : "Drag optional add-ons here"}
                                  </div>
                                ) : (
                                  <SortableContext
                                    items={assignedServices}
                                    strategy={verticalListSortingStrategy}
                                  >
                                    <div className="space-y-1.5">
                                      {assignedServices.map((serviceId) => {
                                        const service = optionalServices.find(
                                          (s) => s.id === serviceId,
                                        );

                                        if (!service) return null;

                                        return (
                                          <SortableServiceItem
                                            key={serviceId}
                                            serviceId={serviceId}
                                            service={service}
                                            onRemove={() =>
                                              handleRemoveService(
                                                applicantId,
                                                serviceId,
                                              )
                                            }
                                            isMandatory={false}
                                            isIncluded={false}
                                          />
                                        );
                                      })}
                                    </div>
                                  </SortableContext>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        </ApplicantDraggable>
                      </ApplicantDropZone>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DndContext>
  );
} 